(() => {
  const elementName = 'ug-jp-spokesperson-campaign';
  if (customElements.get(elementName)) return;

  const loaderURL = 'https://widget.gleamjs.io/e.js';
  let scanQueue = Promise.resolve();
  let activeInstance = null;

  function isLoader(script) {
    try {
      const url = new URL(script.src);
      return url.origin + url.pathname === loaderURL;
    } catch {
      return false;
    }
  }

  function waitForScript(script, owned = false) {
    if (!owned && (script.dataset.loaded === '1' || script.readyState === 'complete' ||
      performance.getEntriesByName(script.src).some((entry) => entry.responseEnd > 0))) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const finish = (error) => {
        clearTimeout(timer);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
        if (error && owned) reject(error);
        else resolve();
      };
      const loaded = () => finish();
      const failed = () => finish(new Error('Gleam embed loader is unavailable'));
      const timer = setTimeout(failed, 12000);
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
    });
  }

  function scanWidget(widget, isAlive) {
    // e.js scans once when executed; it has no public rescan API. Serialize scans
    // so two new instances cannot bootstrap the same anchor concurrently.
    scanQueue = scanQueue.catch(() => {}).then(async () => {
      if (!isAlive() || !widget.querySelector('a.e-widget, a.e-gleam')) return;
      await Promise.all(Array.from(document.scripts).filter(isLoader).map((script) => waitForScript(script)));
      if (!isAlive() || !widget.querySelector('a.e-widget, a.e-gleam')) return;
      const script = document.createElement('script');
      script.src = loaderURL;
      script.async = true;
      const loaded = waitForScript(script, true);
      document.head.appendChild(script);
      try {
        await loaded;
      } finally {
        script.remove();
      }
    });
    return scanQueue;
  }

  class SpokespersonCampaign extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('[data-campaign-dialog]');
      this.layout = this.querySelector('[data-campaign-layout]');
      this.widget = this.querySelector('[data-campaign-widget]');
      this.template = this.querySelector('[data-campaign-widget-template]');
      if (!this.dialog || !this.layout) return;
      this.controller = new AbortController();
      this.backdropPointer = false;
      const signal = this.controller.signal;

      this.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target || target.closest(elementName) !== this) return;
        const opener = target.closest('[data-campaign-open]');
        if (opener) {
          if (opener.matches('[disabled], [aria-disabled="true"]')) return;
          if (opener.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
          event.preventDefault();
          this.open(opener);
        } else if (target.closest('[data-campaign-close]')) {
          event.preventDefault();
          this.close();
        }
      }, { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        // close is queued by the browser; an earlier close must not clean up a
        // dialog that has already reopened in the meantime.
        if (!this.dialog.open) this.restorePage();
      }, { signal });
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPointer = event.isPrimary !== false && event.button === 0 && this.isBackdrop(event);
      }, { signal });
      this.dialog.addEventListener('pointerup', (event) => {
        // A drag from the backdrop into the panel can retarget click to their
        // common ancestor. Keep the release inside content from closing it.
        this.backdropPointer = this.backdropPointer && this.isBackdrop(event);
      }, { signal });
      this.dialog.addEventListener('pointercancel', () => { this.backdropPointer = false; }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropPointer && this.isBackdrop(event)) this.close();
        this.backdropPointer = false;
      }, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target instanceof Element && event.target.contains(this)) this.close(false);
      }, { signal });
      document.addEventListener('error', (event) => {
        if (this.widgetState !== 'loading' || !(event.target instanceof HTMLScriptElement)) return;
        try {
          const url = new URL(event.target.src);
          if (url.origin === 'https://widget.gleamjs.io' && url.pathname === '/' + this.widgetId + '/embed.js') this.failWidget();
        } catch {}
      }, { capture: true, signal });
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
      this.stopWidgetWatch();
      if (this.widgetState === 'loading') this.widgetState = 'failed';
    }

    isBackdrop(event) {
      // The layout fills the viewport; its empty grid/padding is the backdrop.
      // Sidebar, panel and their descendants remain interactive content.
      return event.target === this.dialog || event.target === this.layout;
    }

    open(opener) {
      if (!this.isConnected || !this.dialog || this.dialog.open) return;
      activeInstance?.close(false);
      try {
        this.dialog.showModal();
      } catch {
        return;
      }
      this.returnFocus = opener;
      this.lockedBody = document.body;
      this.previousOverflow = this.lockedBody.style.getPropertyValue('overflow');
      this.previousOverflowPriority = this.lockedBody.style.getPropertyPriority('overflow');
      this.lockedBody.style.setProperty('overflow', 'hidden');
      this.pausedGifts = Array.from(this.querySelectorAll('ug-jp-spokesperson-gifts')).map((gift) => {
        const previous = gift.getAttribute('data-paused');
        gift.setAttribute('data-paused', 'true');
        gift.schedule?.();
        return [gift, previous];
      });
      activeInstance = this;
      this.dialog.querySelector('[data-campaign-close]')?.focus({ preventScroll: true });
      const tracking = window.UGJPTracking;
      tracking?.track('jp_sp_modal_view', { ...tracking.context(opener), destination: 'gleam' });
      this.ensureWidget();
    }

    close(restoreFocus = true) {
      if (this.dialog?.open) this.dialog.close();
      this.restorePage(restoreFocus);
    }

    restorePage(restoreFocus = true) {
      this.backdropPointer = false;
      if (this.lockedBody) {
        if (this.previousOverflow) this.lockedBody.style.setProperty('overflow', this.previousOverflow, this.previousOverflowPriority);
        else this.lockedBody.style.removeProperty('overflow');
        this.lockedBody = null;
      }
      this.pausedGifts?.forEach(([gift, previous]) => {
        if (previous === null) gift.removeAttribute('data-paused');
        else gift.setAttribute('data-paused', previous);
        gift.schedule?.();
      });
      this.pausedGifts = null;
      if (activeInstance === this) activeInstance = null;
      const opener = this.returnFocus;
      this.returnFocus = null;
      if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
    }

    stopWidgetWatch() {
      clearTimeout(this.widgetTimer);
      this.widgetObserver?.disconnect();
      this.widgetObserver = null;
    }

    failWidget() {
      this.stopWidgetWatch();
      this.widgetState = 'failed';
    }

    ensureWidget() {
      if (!this.widget || !(this.template instanceof HTMLTemplateElement)) return;
      if (this.widget.querySelector('iframe')) {
        this.widgetState = 'ready';
        return;
      }
      if (this.widgetState === 'loading') return;
      if (this.widgetState === 'failed') {
        Array.from(this.widget.childNodes).forEach((node) => {
          if (node !== this.template) node.remove();
        });
      }
      let anchor = this.widget.querySelector('a.e-widget, a.e-gleam');
      if (!anchor) {
        this.widget.appendChild(this.template.content.cloneNode(true));
        anchor = this.widget.querySelector('a.e-widget, a.e-gleam');
      }
      if (!anchor) return;
      try {
        const url = new URL(anchor.href);
        if (!/^https?:$/.test(url.protocol)) return;
        if (/^\/[a-z0-9]{5}$/i.test(url.pathname)) url.pathname += '/';
        this.widgetId = url.pathname.match(/^\/([a-z0-9]{5})\//i)?.[1];
        if (!this.widgetId) return;
        anchor.href = url.href;
      } catch {
        return;
      }

      this.widgetState = 'loading';
      const attempt = this.widgetAttempt = (this.widgetAttempt || 0) + 1;
      const controller = this.controller;
      const isAlive = () => this.isConnected && this.controller === controller &&
        this.widgetAttempt === attempt && !controller.signal.aborted;
      const ready = () => {
        if (!isAlive() || !this.widget.querySelector('iframe')) return;
        this.stopWidgetWatch();
        this.widgetState = 'ready';
      };
      this.widgetObserver = new MutationObserver(ready);
      this.widgetObserver.observe(this.widget, { childList: true, subtree: true });
      scanWidget(this.widget, isAlive).then(() => {
        if (!isAlive() || this.widgetState !== 'loading') return;
        ready();
        if (this.widgetState === 'loading') this.widgetTimer = setTimeout(() => this.failWidget(), 15000);
      }).catch(() => {
        if (isAlive()) this.failWidget();
      });
    }
  }

  customElements.define(elementName, SpokespersonCampaign);
})();
